import { type FC, useState } from 'react';
import { InlineEditableText } from '@components/InlineEditableText';
import InputText from '@components/InputText';
import { SAVED_VIEW_NAME_MAX_LENGTH } from '@domain/schemas/saved-view.schema';
import { formatDate } from '@root/helpers/date';

interface SavedViewActiveHeaderProps {
  name: string;
  updatedAt: string;
  /** True while another saved-view update PUT is in flight — confirming then would revert it. */
  isSaving?: boolean;
  onRename: (name: string) => void;
}

export const SavedViewActiveHeader: FC<SavedViewActiveHeaderProps> = ({
  name,
  updatedAt,
  isSaving = false,
  onRename,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  const cancelRename = () => {
    setDraft(name);
    setEditing(false);
  };

  const confirmRename = () => {
    if (draft.trim().length === 0 || isSaving) return;
    onRename(draft.trim());
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-2 pt-3">
      <InlineEditableText
        editMode={editing}
        editor={
          <InputText
            name="saved-view-name"
            dataTestId="text:saved-view-name"
            maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
            className="w-[250px] [&>div>input]:h-8"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmRename();
              if (e.key === 'Escape') cancelRename();
            }}
            autoFocus
          />
        }
        display={<span className="text-info-900 text-lg font-medium">{name}</span>}
        onClick={() => {
          setDraft(name);
          setEditing(true);
        }}
        onCancel={cancelRename}
        onConfirm={confirmRename}
        confirmDisabled={draft.trim().length === 0 || isSaving}
        tooltipContent={name}
        buttonDataTestId="saved-view-rename"
      />
      <span className="text-info-400 ml-2 text-xs">Updated {formatDate(updatedAt, 'D MMMM YYYY HH:mm')}</span>
    </div>
  );
};
